import * as React from 'react';
import { Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import { AccessibilityStore, QueryStore, DataStore } from '../stores';
import Tooltip from './Tooltip';
import { models, helpers } from '@kurtosys/ksys-app-template/dist';
import { computed } from 'mobx';
import { ExtendedInfoOption } from '../models/configuration/ExtendedInfoOption';
import Icon from './Icon';
import { Theme } from '../models/Theme';

const ExtendedInfoWrapper = styled<any, any>(
    'div',
    'extendedInfoWrapper',
    'extendedInfo'
)`
    ${propStyles};
`;

ExtendedInfoWrapper.defaultProps = {
    padding: '0 5px',
    display: 'flex',
};

const ExtendedInfoIconWrapper = styled<any, any>(
    'div',
    'extendedInfoIconWrapper',
    'extendedInfo'
)`
    ${propStyles};
`;

ExtendedInfoIconWrapper.defaultProps = {
    flex: '0 0 auto',
};

const TooltipIcon = styled<any, any>(Icon, 'tooltipIcon', 'extendedInfo')``;

TooltipIcon.defaultProps = {
    height: '16px',
    width: '16px',
};

interface ExtendedInfoProps {
    theme?: Theme;
    themeOverride?: Theme;
    accessibilityStore?: AccessibilityStore;
    queryStore?: QueryStore;
    dataStore?: DataStore;
    shareClass?: models.api.search.IEntity;
    document?: models.api.search.IDocument;
    metricContext?: string;
    describedBy?: string;
}

@inject('accessibilityStore', 'queryStore', 'dataStore')
@observer
class ExtendedInfo extends Component<ExtendedInfoProps> {
    @computed
    get queryContext(): models.query.IQueryContext {
        const { shareClass, document, dataStore } = this.props;
        const { entityType } = dataStore.config;
        const context: models.query.IQueryContext = {
            document,
            entityByType: { [entityType || 'CLSS']: shareClass },
        };
        return context;
    }

    @computed
    get extendedInfoOptionsConfig(): Map<string, ExtendedInfoOption> {
        const { dataStore } = this.props;
        const config = dataStore.config.extendedInfoOptions;
        return config;
    }

    @computed
    get extendedInfoOptions(): Array<{
        content: Array<string>;
        iconFilename?: string;
        isDisabled?: boolean;
        ariaLabel?: string;
    }> {
        const { queryStore } = this.props;
        const optionsValues: Array<{
            content: Array<string>;
            iconFilename?: string;
            isDisabled?: boolean;
            ariaLabel?: string;
        }> = [];
        if (this.extendedInfoOptionsConfig) {
            const executionOptions = queryStore.getExecutionOptionsUsingContext(
                this.queryContext
            );
            // Set translate to null for the conditionalOptions so that the conditions are
            // matched on the original values and not the translated values
            const conditionalOptions = {
                executionOptions: { ...executionOptions, translate: null },
            };
            this.extendedInfoOptionsConfig.forEach(option => {
                let showContent = !!!option.conditional;
                if (option.conditional) {
                    const conditionalHelper = new helpers.ConditionalHelper(
                        option.conditional
                    );
                    showContent = conditionalHelper.matchesWithOptions(
                        conditionalOptions
                    );
                }
                if (showContent) {
                    const content = queryStore.execute(
                        option.contentQuery,
                        executionOptions
                    );
                    if (content) {
                        optionsValues.push({
                            content: [content],
                            iconFilename: option.iconFilename,
                            isDisabled: false,
                            ariaLabel: option.ariaLabel,
                        });
                    }
                } else if (option.disabledIconFilename) {
                    optionsValues.push({
                        content: [''],
                        iconFilename: option.disabledIconFilename,
                        isDisabled: true,
                        ariaLabel: option.ariaLabel,
                    });
                }
            });
        }
        return optionsValues;
    }

    render() {
        const {
            theme,
            themeOverride,
            metricContext,
            describedBy,
        } = this.props;

        if (
            !this.extendedInfoOptionsConfig ||
            this.extendedInfoOptions?.length === 0
        ) {
            return null;
        }

        return (
            <ExtendedInfoWrapper
                {...theme.ExtendedInfoWrapper}
                // Passed through props from the implementing component to allow us to override the styling based on the implementation location
                {...themeOverride.ExtendedInfoWrapper}
            >
                {this.extendedInfoOptions.map((option, index) => {
                    return (
                        <ExtendedInfoIconWrapper
                            key={`ExtendedInfo_${index}`}
                            {...theme.ExtendedInfoIconWrapper}
                            // Passed through props from the implementing component to allow us to override the styling based on the implementation location
                            {...themeOverride.ExtendedInfoIconWrapper}
                        >
                            {option.isDisabled ? (
                                <TooltipIcon
                                    aria-hidden={true}
                                    fileName={option.iconFilename}
                                    rowAccessibleContext={describedBy}
                                    {...theme.DisabledTooltipIcon}
                                />
                            ) : (
                                <Tooltip
                                    content={option.content}
                                    iconFilename={option.iconFilename}
                                    accessibilityContext={{
                                        key: option.ariaLabel
                                    }}
                                    rowAccessibleContext={describedBy}
                                    analyticsContext={{
                                        metricContext,
                                        source: 'ExtendedInfoIconWrapper',
                                        // Extended Info is either static or queryable, so no disclaimer type is available to set, defaulting to: extended_information
                                        tooltipCode: 'extended_information',
                                    }}
                                />
                            )}
                        </ExtendedInfoIconWrapper>
                    );
                })}
            </ExtendedInfoWrapper>
        );
    }
}

export default withTheme(ExtendedInfo) as any;



// WEBPACK FOOTER //
// ./src/Components/ExtendedInfo.tsx